import React, { useState, useEffect } from "react";
import { Loader, Image, Button, Input } from "semantic-ui-react";
import { useDropzone } from "react-dropzone";
import { toast } from "react-toastify";
import { HeaderPage } from "../../components/Admin";
import { useSiteConfig, useAuth } from "../../hooks";
import defaultDeveloperLogo from "../../assets/tallansoft.png";

// Subida de una imagen que se guarda sola al elegirla (mismo patrón que el
// logo: sin botón "Guardar" aparte — ver CLAUDE.md, sección SiteConfig).
function QrUpload(props) {
  const { label, field, current, onUpload } = props;
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  const { getRootProps, getInputProps } = useDropzone({
    accept: "image/jpeg, image/png",
    noKeyboard: true,
    multiple: false,
    onDrop: async (files) => {
      const file = files[0];
      setPreview(URL.createObjectURL(file));
      setUploading(true);
      try {
        await onUpload({ [field]: file });
        toast.success(`QR de ${label} actualizado`);
      } catch (error) {
        console.error(error);
        toast.error(`No se pudo subir el QR de ${label}. Intenta de nuevo.`);
      } finally {
        setUploading(false);
      }
    },
  });

  const image = preview || current;
  return (
    <div style={{ flex: "1 1 200px", maxWidth: 240 }}>
      <label style={{ display: "block", fontWeight: 700, marginBottom: 6 }}>
        QR de {label}
      </label>
      <Button type="button" fluid loading={uploading} {...getRootProps()}>
        {image ? "Cambiar QR" : "Subir QR"}
      </Button>
      <input {...getInputProps()} />
      {image && <Image src={image} style={{ marginTop: 10 }} />}
    </div>
  );
}

// Logo + nombre, con el logo guardándose solo al elegirlo y el nombre con su
// botón "Guardar". Se usa dos veces: negocio (logo 2) y desarrollador (logo 1).
function BrandEditor(props) {
  const { title, hint, logoField, nameField, logo, name, fallbackLogo, onUpdate } = props;
  const [previewImage, setPreviewImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [value, setValue] = useState(name || "");
  const [savingName, setSavingName] = useState(false);

  useEffect(() => setValue(name || ""), [name]);

  const onDrop = async (acceptedFile) => {
    const file = acceptedFile[0];
    setPreviewImage(URL.createObjectURL(file));
    setUploading(true);
    try {
      await onUpdate({ [logoField]: file });
      toast.success("Logo actualizado");
    } catch (error) {
      console.error(error);
      toast.error("No se pudo subir el logo. Intenta de nuevo.");
    } finally {
      setUploading(false);
    }
  };

  const onSaveName = async () => {
    setSavingName(true);
    try {
      await onUpdate({ [nameField]: value });
      toast.success("Nombre actualizado");
    } catch (error) {
      console.error(error);
      toast.error("No se pudo actualizar el nombre. Intenta de nuevo.");
    } finally {
      setSavingName(false);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: "image/jpeg, image/png",
    noKeyboard: true,
    multiple: false,
    onDrop,
  });

  const image = previewImage || logo || fallbackLogo;

  return (
    <div style={{ flex: "1 1 280px", maxWidth: 320 }}>
      <h3 style={{ marginBottom: 4 }}>{title}</h3>
      <p style={{ color: "#777", marginBottom: 12 }}>{hint}</p>
      <Button type="button" fluid loading={uploading} {...getRootProps()}>
        {logo || previewImage ? "Cambiar logo" : "Subir logo"}
      </Button>
      <input {...getInputProps()} />

      {image && (
        <div
          style={{
            margin: "15px 0",
            padding: 12,
            backgroundColor: "#e67e22",
            borderRadius: 4,
            textAlign: "center",
          }}
        >
          <Image src={image} style={{ maxHeight: 120, margin: "0 auto" }} />
        </div>
      )}

      <label style={{ display: "block", marginTop: 20, marginBottom: 4, fontWeight: 700 }}>
        Nombre
      </label>
      <Input fluid value={value} onChange={(_, data) => setValue(data.value)} />
      <Button
        primary
        fluid
        loading={savingName}
        style={{ marginTop: 10 }}
        onClick={onSaveName}
      >
        Guardar nombre
      </Button>
    </div>
  );
}

// Admin: solo el logo y nombre del negocio (logo 2). Súper Usuario: además
// el del desarrollador (logo 1, barra superior) — el backend lo refuerza.
export function SiteConfigAdmin() {
  const { loading, siteConfig, getSiteConfig, updateSiteConfig } =
    useSiteConfig();
  const { auth } = useAuth();

  useEffect(() => {
    getSiteConfig();
  }, []);

  return (
    <>
      <HeaderPage title="Logo y nombre" />

      {loading && !siteConfig ? (
        <Loader active inline="centered">
          Cargando...
        </Loader>
      ) : (
        <div style={{ display: "flex", gap: 48, flexWrap: "wrap" }}>
          <BrandEditor
            title={auth.me?.is_superuser ? "Negocio (logo 2)" : "Logo del negocio"}
            hint="Tu restaurante: va en la cabecera del menú ☰, en la pantalla del comensal, en los tickets y en el ícono de la app."
            logoField="logo"
            nameField="site_name"
            logo={siteConfig?.logo}
            name={siteConfig?.site_name}
            onUpdate={updateSiteConfig}
          />
          {auth.me?.is_superuser && (
            <BrandEditor
              title="Desarrollador (logo 1)"
              hint="Marca del sistema: va en la barra superior del panel. Solo el Súper Usuario puede cambiarla."
              logoField="developer_logo"
              nameField="developer_name"
              logo={siteConfig?.developer_logo}
              name={siteConfig?.developer_name}
              fallbackLogo={defaultDeveloperLogo}
              onUpdate={updateSiteConfig}
            />
          )}
        </div>
      )}
    </>
  );
}

// Pantalla aparte del logo: la ve también el rol Admin (el backend solo le
// deja cambiar yape_qr/plin_qr — ver siteconfig/api/views.py).
export function PaymentQrAdmin() {
  const { loading, siteConfig, getSiteConfig, updateSiteConfig } =
    useSiteConfig();

  useEffect(() => {
    getSiteConfig();
  }, []);

  return (
    <>
      <HeaderPage title="QR de pago (Yape / Plin)" />

      {loading ? (
        <Loader active inline="centered">
          Cargando...
        </Loader>
      ) : (
        // QR de cobro: se muestran al cajero al cobrar con Yape/Plin.
        <div>
          <p style={{ color: "#777", marginBottom: 14 }}>
            La imagen del QR que te da la app Yape / Plin de tu negocio. Se
            muestra al cobrar con esa billetera para que el cliente lo escanee.
          </p>
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            <QrUpload
              label="Yape"
              field="yape_qr"
              current={siteConfig?.yape_qr}
              onUpload={updateSiteConfig}
            />
            <QrUpload
              label="Plin"
              field="plin_qr"
              current={siteConfig?.plin_qr}
              onUpload={updateSiteConfig}
            />
          </div>
        </div>
      )}
    </>
  );
}
